import { useFormContext, Controller, useWatch } from "react-hook-form";
import Input from "components/Input";
import PaymentSpeedInput from "components/PaymentSpeedInput";
import DollarInput from "components/DollarInput";
import Textarea from "components/Textarea";
import { useFundingRequestForm } from "./FundingRequestFormContext";
import type { IEditFundingRequest } from "features/main/HaulPay/Shared/EditFundingRequest/Manage";
import { IOption } from "../../../../../../models/Form";
import { PERCENTAGE_LIST } from "../../../../../../constants/haulpay-broker";
import { FUNDING_REQUEST_CONTRACT_TYPES } from "../../../../../../helpers/constantTypes";
import { IPaymentProfile } from "../../../../../../models/HaulPay";
import { useEffect } from "react";
import CarrierSelector from "../../CarrierSelector";
import GoogleAutoComplete from "../../../../../../components/GoogleAutoComplete";
import { CARRIER_PAYMENT_SPEED } from "../../../../../../constants/haulpay-carrier";
import clsx from "clsx";
import Select from "../../../../../../components/Select";
import { useUserFeature } from "../../../../../../hooks/useUserFeature";
import {
  canBrokerEditApprovedFactoredFR,
  canBrokerEditApprovedSelfFinanceOffPlatformFR,
  canBrokerEditApprovedSelfFinanceCarrierFields
} from "./permissions";
import ControlledTooltip from "./ControlledTooltip";

export default function CarrierInformation() {
  const {
    isGlobalDisabled,
    loading,
    selectedPaymentProfile,
    setSelectedPaymentProfile,
    hasFactoringCompany,
    selectedFR,
    isOtherFieldsDisabled,
    otherFieldsDisabledTooltip
  } = useFundingRequestForm();
  const {
    control,
    register,
    getValues,
    setValue,
    formState: { errors }
  } = useFormContext<Partial<IEditFundingRequest>>();

  const contractType = useWatch({ control: control, name: "fundingRequestType" });

  const { isBroker } = useUserFeature();

  const isNonFactoredFR = [
    FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_STANDARD,
    FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_BILL_OUT
  ].includes(contractType?.value ?? "");

  const allowBrokerEditApprovedFR = canBrokerEditApprovedFactoredFR({
    isBroker,
    status: selectedFR?.status,
    debtorName: selectedFR?.debtor?.name ?? null,
    isNonFactoredFR
  });

  const allowOffPlatformCarrierEdit = canBrokerEditApprovedSelfFinanceOffPlatformFR({
    isBroker,
    status: selectedFR?.status,
    contractType: selectedFR?.contractType,
    carrierEditLocked: selectedFR?.carrierEditLocked,
    carrierFundingCommitted: selectedFR?.carrierFundingCommitted,
    receivableStatus: selectedFR?.receivableStatus
  }).canEditCarrierFields;

  // HP-8882: carrier-side edits (carrier selection, amount, speed, portion) are allowed on any
  // approved self-finance FR (no-invoice / with-invoice / off-platform) while the carrier is unpaid.
  const allowSelfFinanceCarrierEdit = canBrokerEditApprovedSelfFinanceCarrierFields({
    isBroker,
    status: selectedFR?.status,
    contractType: selectedFR?.contractType,
    carrierEditLocked: selectedFR?.carrierEditLocked,
    carrierFundingCommitted: selectedFR?.carrierFundingCommitted
  });

  // Define custom input class for consistent styling
  const inputClass =
    "placeholder:text-slate-400 text-slate-700 !text-sm border border-slate-200 !rounded !px-4 !py-2 !h-10 transition duration-300 ease focus:outline-none focus:border-slate-400 hover:border-slate-300 shadow-sm focus:shadow";
  const prefixUnitClass =
    "!px-3 !py-[0.25rem] flex items-center justify-center font-thin !rounded !rounded-r-none !h-10";
  // const suffixUnitClass =
  //   "!px-3 !py-[0.25rem] flex items-center justify-center font-thin !rounded !rounded-l-none !h-10";
  const textareaClass = "text-white rounded w-full";

  const setPaymentProfile = (paymentProfile: IPaymentProfile) => {
    setSelectedPaymentProfile(paymentProfile);
    setValue("paymentProfile", paymentProfile.company_name);
  };

  useEffect(() => {
    if (selectedPaymentProfile) {
      setValue("carrierEmail", selectedPaymentProfile?.owner_email ?? "");
      setValue("carrierPhone", selectedPaymentProfile?.owner_phone_number_cell ?? "");
      setValue("carrierMc", selectedPaymentProfile?.company_mc ?? "");
      setValue("factoringCompanyName", selectedPaymentProfile.factoring_company?.name ?? "");
      if (selectedPaymentProfile.factoring_company?.name) {
        setValue(
          "carrierPaymentMethod",
          `${selectedPaymentProfile.factoring_company.name} (**** ${selectedPaymentProfile.factoring_company.accountNumber})`
        );
        setValue("paymentProfileDisplay", selectedPaymentProfile.factoring_company.method);
        setValue("brokersQRFee", PERCENTAGE_LIST[2]);
      } else {
        if ((selectedPaymentProfile.accounts ?? []).length) {
          setValue(
            "carrierPaymentMethod",
            `${selectedPaymentProfile.accounts[0]?.method} (**** ${selectedPaymentProfile.accounts[0]?.accountNumberRedacted})`
          );
          setValue("paymentProfileDisplay", selectedPaymentProfile.accounts[0]?.method);
        } else {
          setValue("paymentProfileDisplay", "ACH");
          setValue("carrierPaymentMethod", "ACH");
        }
        setValue(
          "brokersQRFee",
          PERCENTAGE_LIST.find(
            ({ amount }) => amount === Number(selectedPaymentProfile.factoring_fee_split_rate)
          )
        );
      }
      setValue(
        "carrierPaymentSpeed",
        CARRIER_PAYMENT_SPEED.find((item) => item.value === selectedPaymentProfile.payout_days)
      );
    } else {
      setValue("carrierEmail", "");
      setValue("carrierPhone", "");
      setValue("carrierMc", "");
      setValue("factoringCompanyName", "");
    }
  }, [selectedPaymentProfile]);

  if (loading) {
    return (
      <div className="animate-pulse flex flex-col gap-4">
        <div className="h-10 bg-foreground rounded-md"></div>
        <div className="h-10 bg-foreground rounded-md"></div>
        <div className="h-10 bg-foreground rounded-md"></div>
      </div>
    );
  }

  return (
    <div className="p-3 rounded-lg">
      <div className="space-y-6">
        <ControlledTooltip
          id="carrier-info-carrier"
          message={otherFieldsDisabledTooltip}
          position="down"
          tooltipContainerClassName="relative"
          wrapperClass="max-w-xs !whitespace-normal"
          disabled={!isOtherFieldsDisabled}>
          <div>
            <h6 className="font-normal mb-2">Carrier</h6>
            <CarrierSelector
              name="paymentProfile"
              register={register}
              errors={errors}
              getValues={getValues}
              setPaymentProfile={setPaymentProfile}
              isSearchCompanyName
              useGroupIC
              inputClass={inputClass}
              disabled={
                isOtherFieldsDisabled || (allowSelfFinanceCarrierEdit ? false : isGlobalDisabled)
              }
            />
          </div>
        </ControlledTooltip>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h6 className="font-normal mb-2">Carrier MC</h6>
            <Controller
              name="carrierMc"
              control={control}
              render={({ field }) => (
                <Input
                  label=""
                  {...field}
                  placeholder="MC-*******"
                  disabled
                  inputClass={inputClass}
                />
              )}
            />
          </div>
          <div>
            <h6 className="font-normal mb-2 ">Carrier DOT</h6>
            <Controller
              name="carrierDot"
              control={control}
              render={({ field }) => (
                <Input
                  label=""
                  {...field}
                  placeholder="DOT-*******"
                  disabled
                  inputClass={inputClass}
                />
              )}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <ControlledTooltip
            id="carrier-info-invoice-amount"
            message={otherFieldsDisabledTooltip}
            position="down"
            tooltipContainerClassName="relative"
            wrapperClass="max-w-xs !whitespace-normal"
            disabled={!isOtherFieldsDisabled}>
            <div>
              <h6 className="font-normal mb-2">Carrier Invoice Amnt.</h6>
              <Controller
                name="carrierInvoiceAmount"
                control={control}
                render={({ field }) => (
                  <DollarInput
                    label=""
                    {...field}
                    disabled={
                      isOtherFieldsDisabled ||
                      (allowOffPlatformCarrierEdit || allowSelfFinanceCarrierEdit
                        ? false
                        : isGlobalDisabled)
                    }
                    inputClass={inputClass}
                    prefixUnitClass={prefixUnitClass}
                  />
                )}
              />
            </div>
          </ControlledTooltip>
          <ControlledTooltip
            id="carrier-info-pro-number"
            message={otherFieldsDisabledTooltip}
            position="down"
            tooltipContainerClassName="relative"
            wrapperClass="max-w-xs !whitespace-normal"
            disabled={!isOtherFieldsDisabled}>
            <div>
              <h6 className="font-normal mb-2 ">Carrier Pro #</h6>
              <Controller
                name="carrierPro"
                control={control}
                render={({ field }) => (
                  <Input
                    label=""
                    {...field}
                    placeholder="PRO-*******"
                    disabled={isGlobalDisabled || isOtherFieldsDisabled}
                    inputClass={inputClass}
                  />
                )}
              />
              <p className="text-sm italic text-info mt-1">Visible to Carrier Only</p>
            </div>
          </ControlledTooltip>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <ControlledTooltip
            id="carrier-info-payment-speed"
            message={otherFieldsDisabledTooltip}
            position="down"
            tooltipContainerClassName="relative"
            wrapperClass="max-w-xs !whitespace-normal"
            disabled={!isOtherFieldsDisabled}>
            <div>
              <h6 className="font-normal mb-2">Carrier Payment Speed</h6>
              <PaymentSpeedInput
                control={control}
                name="carrierPaymentSpeed"
                customOnChange={(paymentSpeed) => {
                  if (hasFactoringCompany) {
                    setValue("brokersQRFee", PERCENTAGE_LIST[2], { shouldDirty: true });
                    return;
                  }

                  const splitFee = (paymentSpeed as any)?.splitFee as string | undefined;

                  const match = PERCENTAGE_LIST.find((obj) => obj.value === String(splitFee)) as
                    | IOption
                    | undefined;
                  setValue("brokersQRFee", match ?? PERCENTAGE_LIST[0], { shouldDirty: true });
                }}
                inputProps={{
                  disabled:
                    isOtherFieldsDisabled ||
                    (isNonFactoredFR ||
                    allowBrokerEditApprovedFR ||
                    allowOffPlatformCarrierEdit ||
                    allowSelfFinanceCarrierEdit
                      ? false
                      : isGlobalDisabled),
                  direction: "up",
                  inputClass: inputClass
                }}
                noLabel
              />
            </div>
          </ControlledTooltip>
          {!isNonFactoredFR && (
            <ControlledTooltip
              id="carrier-info-qp-fee"
              message={otherFieldsDisabledTooltip}
              position="down"
              tooltipContainerClassName="relative"
              wrapperClass="max-w-xs !whitespace-normal"
              disabled={!isOtherFieldsDisabled}>
              <div>
                <h6 className="font-normal mb-2">Broker&apos;s % of Carrier QP Fee</h6>
                <Controller
                  name="brokersQRFee"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Select
                      options={PERCENTAGE_LIST}
                      value={value}
                      disabled={
                        isOtherFieldsDisabled ||
                        hasFactoringCompany ||
                        (isNonFactoredFR ||
                        allowBrokerEditApprovedFR ||
                        allowOffPlatformCarrierEdit ||
                        allowSelfFinanceCarrierEdit
                          ? false
                          : isGlobalDisabled)
                      }
                      onChange={onChange}
                      inputClass={inputClass}
                    />
                  )}
                />
                <p className="text-sm italic text-info mt-1">
                  (Carrier Rate will change depending on the portion % you absorb)
                </p>
              </div>
            </ControlledTooltip>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h6 className="font-normal mb-2">Carrier Payment Method</h6>
            <Controller
              name="carrierPaymentMethod"
              control={control}
              render={({ field }) => (
                <Input
                  label=""
                  {...field}
                  placeholder="Carrier Payment Method"
                  disabled
                  inputClass={inputClass}
                />
              )}
            />
          </div>
          <div>
            <h6 className="font-normal mb-2">Factoring Company</h6>
            <Controller
              name="factoringCompanyName"
              control={control}
              render={({ field }) => (
                <Input label="" {...field} placeholder="N/A" disabled inputClass={inputClass} />
              )}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <ControlledTooltip
            id="carrier-info-first-pickup"
            message={otherFieldsDisabledTooltip}
            position="down"
            tooltipContainerClassName="relative"
            wrapperClass="max-w-xs !whitespace-normal"
            disabled={!isOtherFieldsDisabled}>
            <div>
              <h6 className="font-normal mb-2">First Pick up</h6>
              <Controller
                name="firstPickUp"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <GoogleAutoComplete
                    placeholder="First Pick up"
                    value={value}
                    onChange={onChange}
                    disabled={isGlobalDisabled || isOtherFieldsDisabled}
                    error={errors.firstPickUp?.country as any}
                    inputClass={inputClass}
                  />
                )}
              />
            </div>
          </ControlledTooltip>
          <ControlledTooltip
            id="carrier-info-final-delivery"
            message={otherFieldsDisabledTooltip}
            position="down"
            tooltipContainerClassName="relative"
            wrapperClass="max-w-xs !whitespace-normal"
            disabled={!isOtherFieldsDisabled}>
            <div>
              <h6 className="font-normal mb-2">Final Delivery</h6>
              <Controller
                name="finalDelivery"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <GoogleAutoComplete
                    placeholder="Final Delivery"
                    value={value}
                    onChange={onChange}
                    disabled={isGlobalDisabled || isOtherFieldsDisabled}
                    error={errors.finalDelivery?.country as any}
                    inputClass={inputClass}
                  />
                )}
              />
            </div>
          </ControlledTooltip>
        </div>
        <ControlledTooltip
          id="carrier-info-invoice-details"
          message={otherFieldsDisabledTooltip}
          position="down"
          tooltipContainerClassName="relative"
          wrapperClass="max-w-xs !whitespace-normal"
          disabled={!isOtherFieldsDisabled}>
          <div className="pt-4">
            <h6 className="font-normal mb-2">Carrier Invoice Details</h6>
            <Controller
              name="carrierInvoiceDetails"
              control={control}
              render={({ field }) => (
                <Textarea
                  className={textareaClass}
                  inputClass={clsx(
                    "mobile:min-h-[200px]",
                    "disabled:cursor-not-allowed",
                    "disabled:bg-inputDark"
                  )}
                  rows={2}
                  placeholder="Carrier Invoice Details"
                  {...field}
                  disabled={isGlobalDisabled || isOtherFieldsDisabled}
                />
              )}
            />
            <p className="text-sm italic text-info mt-1">Visible to Carrier Only</p>
          </div>
        </ControlledTooltip>
      </div>
    </div>
  );
}
